import React from 'react';
import { Search, Filter, Download } from 'lucide-react';
import { SearchInput } from './SearchInput';
import { StatusFilter } from './StatusFilter';
import { CountryFilter } from './CountryFilter';
import { ActiveFilters } from './ActiveFilters';
import { useLanguage } from '../../../../contexts/LanguageContext';
import { exportPilgrimGuideLinks } from '../../../../utils/exportPilgrimGuide';
import toast from 'react-hot-toast';
import { PilgrimGuideLink } from '../../../../types/pilgrimGuide';

type Props = {
  searchTerm: string;
  onSearchChange: (value: string) => void;
  selectedStatus: string;
  onStatusChange: (value: string) => void;
  selectedCountry: string;
  onCountryChange: (value: string) => void;
  selectedOwnership: string;
  onOwnershipChange: (value: string) => void;
  countries: string[];
  filteredLinks: PilgrimGuideLink[];
};

export const TableFilters = ({
  searchTerm,
  onSearchChange,
  selectedStatus,
  onStatusChange,
  selectedCountry,
  onCountryChange,
  selectedOwnership,
  onOwnershipChange,
  countries,
  filteredLinks
}: Props) => {
  const { translations } = useLanguage();

  const handleExport = () => {
    const success = exportPilgrimGuideLinks(filteredLinks);
    if (success) {
      toast.success(translations?.pilgrimGuide?.exportSuccess || 'Data exported successfully');
    } else {
      toast.error(translations?.pilgrimGuide?.exportError || 'Failed to export data');
    }
  };

  return (
    <div className="p-4 border-b space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {/* Search Input - Full width on first row on desktop */}
        <div className="md:col-span-3">
          <SearchInput 
            value={searchTerm}
            onChange={onSearchChange}
          />
        </div>
        
        {/* Export Button - Last column of first row */}
        <div className="flex items-center justify-end">
          <button
            onClick={handleExport}
            className="w-full md:w-auto flex items-center justify-center gap-2 px-4 py-2 bg-primary-50 text-primary-600 rounded-lg hover:bg-primary-100 transition-colors"
          >
            <Download size={20} />
            <span>{translations?.pilgrimGuide?.exportButton || 'Export Data'}</span>
          </button>
        </div>
        
        {/* Filters - Second row, spaced evenly */}
        <div className="md:col-span-4 grid grid-cols-1 sm:grid-cols-3 gap-4">
          <StatusFilter 
            value={selectedStatus}
            onChange={onStatusChange}
          />
          
          <CountryFilter 
            value={selectedCountry}
            onChange={onCountryChange}
            countries={countries}
          />
          
          {/* Ownership Filter */}
          <select
            value={selectedOwnership}
            onChange={(e) => onOwnershipChange(e.target.value)}
            className="px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
          >
            <option value="all">
              {translations?.filters?.allOwnership || 'All Ownership'}
            </option>
            <option value="owner">
              {translations?.filters?.isOwner || 'Is Owner'}
            </option>
            <option value="notOwner">
              {translations?.filters?.notOwner || 'Not Owner'}
            </option>
          </select>
        </div>
      </div>

      <ActiveFilters
        filters={{
          search: searchTerm,
          status: selectedStatus,
          country: selectedCountry,
          ownership: selectedOwnership
        }}
      />
    </div>
  );
};